/**
 * Trade Alerts — notifications tied to user email
 *
 * Tracks all trade executions and rejections, stores with user email,
 * displays in a persistent notifications panel.
 */

import { useCallback, useEffect, useState, forwardRef, useImperativeHandle } from 'react';

export interface TradeAlert {
  id: string;
  email: string;
  timestamp: number;
  type: 'success' | 'error' | 'pending';
  asset: 'EQUITY' | 'FX' | 'BOND' | 'ETF';
  symbol: string;
  side: 'BUY' | 'SELL';
  quantity: number;
  price: number;
  notional: number;
  message: string;
  dismissed: boolean;
}

interface TradeAlertsProps {
  userEmail?: string;
  onClear?: () => void;
}

export interface TradeAlertsHandle {
  addAlert: (
    asset: 'EQUITY' | 'FX' | 'BOND' | 'ETF',
    symbol: string,
    side: 'BUY' | 'SELL',
    quantity: number,
    price: number,
    notional: number,
    type?: 'success' | 'error',
    message?: string
  ) => void;
}

export const TradeAlerts = forwardRef<TradeAlertsHandle, TradeAlertsProps>(
  function TradeAlerts({ userEmail, onClear }, ref) {
    const [alerts, setAlerts] = useState<TradeAlert[]>([]);
    const [expanded, setExpanded] = useState(false);

  // Load alerts from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(`trade-alerts-${userEmail}`);
    if (stored) {
      try {
        setAlerts(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse alerts:', e);
      }
    }
  }, [userEmail]);

  // Save alerts to localStorage
  useEffect(() => {
    if (userEmail) {
      localStorage.setItem(`trade-alerts-${userEmail}`, JSON.stringify(alerts));
    }
  }, [alerts, userEmail]);

  // Add a trade alert
  const addAlert = useCallback((
    asset: 'EQUITY' | 'FX' | 'BOND' | 'ETF',
    symbol: string,
    side: 'BUY' | 'SELL',
    quantity: number,
    price: number,
    notional: number,
    type: 'success' | 'error' = 'success',
    message?: string
  ) => {
    const alert: TradeAlert = {
      id: `alert-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      email: userEmail || 'anonymous@nigastock.com',
      timestamp: Date.now(),
      type,
      asset,
      symbol,
      side,
      quantity,
      price,
      notional,
      message: message || (type === 'success'
        ? `${side} ${quantity} ${symbol} @ ₦${price.toFixed(2)}`
        : `Failed to execute ${side} ${symbol}`),
      dismissed: false,
    };

    setAlerts((prev) => [alert, ...prev]);

    // Auto-dismiss success alerts after 5 seconds
    if (type === 'success') {
      setTimeout(() => {
        setAlerts((prev) =>
          prev.map((a) => a.id === alert.id ? { ...a, dismissed: true } : a)
        );
      }, 5000);
    }
  }, [userEmail]);

  const dismissAlert = useCallback((id: string) => {
    setAlerts((prev) =>
      prev.map((a) => a.id === id ? { ...a, dismissed: true } : a)
    );
  }, []);

  const clearAll = useCallback(() => {
    setAlerts([]);
    onClear?.();
  }, [onClear]);

  useImperativeHandle(ref, () => ({
    addAlert
  }), [addAlert]);

  const undismissedCount = alerts.filter((a) => !a.dismissed).length;
  const visibleAlerts = alerts.filter((a) => !a.dismissed);

  return (
    <>
      {/* Alert Bell Icon in Top Right */}
      <div style={{
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 999,
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          style={{
            padding: '8px 12px',
            backgroundColor: undismissedCount > 0 ? '#ef4444' : '#6b7280',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
          title="Trade alerts"
        >
          🔔 {undismissedCount > 0 && <span>{undismissedCount}</span>}
        </button>
      </div>

      {/* Alerts Panel */}
      {expanded && (
        <div style={{
          position: 'fixed',
          top: 60,
          right: 16,
          width: '420px',
          maxHeight: '600px',
          backgroundColor: '#1a1f2e',
          border: '1px solid #333',
          borderRadius: '8px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
          zIndex: 999,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid #333',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 600 }}>Trade Alerts</div>
              <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                {userEmail || 'Anonymous'}
              </div>
            </div>
            {alerts.length > 0 && (
              <button
                type="button"
                onClick={clearAll}
                style={{
                  padding: '4px 8px',
                  fontSize: '11px',
                  backgroundColor: 'transparent',
                  color: '#999',
                  border: '1px solid #333',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Clear all
              </button>
            )}
          </div>

          {/* Alerts List */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '8px'
          }}>
            {visibleAlerts.length === 0 ? (
              <div style={{
                padding: '24px 16px',
                textAlign: 'center',
                color: '#666',
                fontSize: '13px'
              }}>
                No active alerts
              </div>
            ) : (
              visibleAlerts.map((alert) => (
                <div
                  key={alert.id}
                  style={{
                    padding: '12px',
                    marginBottom: '8px',
                    backgroundColor: '#252d3d',
                    border: `1px solid ${
                      alert.type === 'success' ? '#10b981' :
                      alert.type === 'error' ? '#ef4444' : '#f59e0b'
                    }`,
                    borderRadius: '6px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: alert.type === 'success' ? '#10b981' :
                             alert.type === 'error' ? '#ef4444' : '#f59e0b'
                    }}>
                      {alert.type === 'success' ? '✓ Executed' :
                       alert.type === 'error' ? '✗ Failed' : '⏳ Pending'}
                    </div>

                    <div style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      marginTop: '4px',
                      color: '#fff'
                    }}>
                      {alert.side} {alert.quantity} {alert.symbol}
                    </div>

                    <div style={{
                      fontSize: '12px',
                      color: '#999',
                      marginTop: '4px'
                    }}>
                      @ ₦{alert.price.toFixed(2)} • ₦{alert.notional.toLocaleString('en-NG', { maximumFractionDigits: 0 })}
                    </div>

                    <div style={{
                      fontSize: '11px',
                      color: '#666',
                      marginTop: '6px'
                    }}>
                      {new Date(alert.timestamp).toLocaleTimeString('en-NG', {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                      })} • {alert.asset}
                    </div>

                    {alert.message && (
                      <div style={{
                        fontSize: '11px',
                        color: '#999',
                        marginTop: '4px',
                        fontStyle: 'italic'
                      }}>
                        {alert.message}
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => dismissAlert(alert.id)}
                    style={{
                      padding: '4px 8px',
                      fontSize: '12px',
                      backgroundColor: 'transparent',
                      color: '#999',
                      border: 'none',
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Stats Footer */}
          {alerts.length > 0 && (
            <div style={{
              padding: '12px 16px',
              borderTop: '1px solid #333',
              fontSize: '11px',
              color: '#999',
              backgroundColor: '#1a1f2e'
            }}>
              Total trades: {alerts.length} • Success: {alerts.filter(a => a.type === 'success').length}
            </div>
          )}
        </div>
      )}

    </>
  );
}
);

export default TradeAlerts;
